import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Landmark, Loader2 } from 'lucide-react';
import { Button, Card, CardBody } from '@solara/ui-shared';
import { useCompleteEmaraTax } from '@solara/api-client/react';

/**
 * EmaraTax authorization callback. The FTA EmaraTax portal redirects here with
 * `?code=...&state=...` after the taxpayer authorizes Solara as their ASP.
 * This is the official FTA-controlled inbound path. We exchange the code and
 * record the verification, then return the user to onboarding.
 *
 * `channel=inbound` when the taxpayer started on EmaraTax and picked us;
 * `outbound` when they started in our portal and we sent them out. We default
 * to `outbound` since most arrivals here came from our own "Verify" button, and
 * accept `?channel=inbound` when the FTA deep-link carries it.
 */
export default function EmaraTaxCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const complete = useCompleteEmaraTax();
  const [state, setState] = useState<'working' | 'done' | 'error'>('working');
  const [detail, setDetail] = useState<string>('');
  const ran = useRef(false);

  const code = params.get('code');
  const channel = params.get('channel') === 'inbound' ? 'inbound' : 'outbound';

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!code) {
      setState('error');
      setDetail('No authorization code was returned by EmaraTax.');
      return;
    }
    complete
      .mutateAsync({ code, channel })
      .then((res) => {
        setState('done');
        setDetail(res.emaraVerificationId);
      })
      .catch((e) => {
        setState('error');
        setDetail(e instanceof Error ? e.message : 'Verification failed.');
      });
  }, [code, channel, complete]);

  return (
    <div className="max-w-lg mx-auto py-12">
      <Card className="rounded-2xl">
        <CardBody className="p-8 text-center space-y-4">
          <div className="mx-auto h-14 w-14 rounded-full grid place-items-center bg-brand-50 text-brand-700">
            <Landmark className="h-7 w-7" />
          </div>
          <h1 className="text-xl font-semibold">EmaraTax authorization</h1>

          {state === 'working' ? (
            <div className="flex items-center justify-center gap-2 text-fg-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Exchanging authorization code…
            </div>
          ) : state === 'done' ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-success">
                <CheckCircle2 className="h-5 w-5" />
                Verified ({channel} channel)
              </div>
              <div className="text-xs text-fg-muted font-mono">{detail}</div>
              <Button onClick={() => navigate('/onboarding')}>Continue onboarding</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-danger">
                <AlertTriangle className="h-5 w-5" />
                {detail}
              </div>
              <Button asChild variant="outline">
                <Link to="/onboarding">Back to onboarding</Link>
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
